/**
 * ONE HOME, in the client page's shape (ops #84, `docs/plan-client-workplace.md` section 5): the owner's
 * "a neat screen for a single client and or property", the property half.
 *
 * THE HOUSEHOLD FIRST, and the way back to it, because a home is always somebody's. Then the home as a
 * person at its door needs it: the address, the label, the county, the gate and the vendor
 * registration, the way to the door, the pets, and the do-not list drawn as its own panel present or
 * absent, as the client page draws it, since an absent panel reads as an empty list and an empty list
 * reads as permission.
 *
 * THE CODES CARD IS THE CLIENT PAGE'S OWN (`HomeCodes`): labels listed, a value only through the reveal,
 * which asks for its fresh sign-in as it always has. The home's read carries no code at all.
 *
 * WHAT THEY CHECK FIRST (ops #86), the three things the household looks at first, with the one edit this
 * page makes: the office types them on the household's word, and the next sheet opened freezes them.
 *
 * THE HOME PLAN (ops #74 item 6), the home in the household's own words: the version approved, which
 * the next sheet opened freezes, and the draft the office typed, which no sheet shows until somebody
 * approves it. Written from a call or a walkthrough, approved by the id read, a code refused by the
 * server. A read that failed says so and offers nothing to write over.
 *
 * THE KEYS (ops #100), the body of the card `HomeKeys`: where each key the household gave is now, at
 * the office, with a named cleaner or gone, one form to record a movement, and the chain under it,
 * collapsed. The server holds every rule and says each refusal in its own sentence.
 *
 * THE AGREEMENTS, each opening its own page, where a cadence is changed, a pause is set and an ending is
 * recorded, with the price the published card gave it: nothing on this page edits a price. A new
 * agreement is set up on the household's page, on the home's card.
 *
 * THE NEXT CLEANS OF THIS HOME, each opening its visit, and THE HOUSEHOLD'S PEOPLE with tap-to-call, a
 * number marked do not call drawn as one, as everywhere else.
 *
 * AND THE TIMELINE WITH THIS HOME FIXED, behind the same press and the same fresh sign-in as the
 * household's (`TimelinePanel`), with the Household chip that adds the household's own rows.
 *
 * Office only on the server (`GET /api/homes/:id` refuses a cleaner); the cleaner's branch of the app
 * has no route to it, which is UX and never the control. An ended household's home opens, for the
 * record, and says the household ended.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AGREEMENT_STATE_LABELS,
  CADENCE_LABELS,
  HOME_PLAN_SOURCE_LABELS,
  OFFICE_HOME_PLAN_SOURCES,
  PRICE_SOURCE_LABELS,
  SERVICE_LINE_LABELS,
  VISIT_STATUS_LABELS,
  checksFirstLines,
  humanise,
  type OfficeHomePlanSource,
} from '@fk/shared';
import {
  ApiError,
  approveHomePlan,
  getHome,
  getHomePlan,
  setChecksFirst,
  writeHomePlan,
  type AgreementLine,
  type HomePlan,
  type HomePlanVersion,
  type HomeRecord,
  type Me,
  type OpenClean,
} from '../api.ts';
import { Card, Detail, Pill, ScreenTitle } from '../components/AppShell.tsx';
import { CallLink, MailLink } from '../components/CallLink.tsx';
import { CallThrough } from '../components/CallThrough.tsx';
import { ChecksFirstBoxes, joinChecksFirst } from '../components/ChecksFirstBoxes.tsx';
import { DoNotPanel } from '../components/DoNotPanel.tsx';
import { Button, SelectField, TextAreaField } from '../components/Field.tsx';
import { HomeCodes } from '../components/HomeCodes.tsx';
import { HomeKeys } from '../components/HomeKeys.tsx';
import { pauseWords } from '../components/AtAGlance.tsx';
import { Busy, ErrorNotice, SavedNotice } from '../components/Notice.tsx';
import { agreementStateTone } from '../components/StatusPills.tsx';
import { TimelinePanel } from '../components/TimelinePanel.tsx';
import { refusedFieldsOf, useRefusedBoxes } from '../components/WordsCheck.tsx';
import { STATE, formatWindow, mediumDay, money, shortDay } from '../format.ts';

function addressOf(home: HomeRecord['home']): string {
  const lines = [home.addressLine1];
  if (home.addressLine2) lines.push(home.addressLine2);
  lines.push(`${home.town}, ${STATE} ${home.postalCode}`);
  return lines.join('\n');
}

function sizeOf(home: HomeRecord['home']): string | null {
  const parts: string[] = [];
  if (home.bedrooms !== null) parts.push(`${home.bedrooms} bed`);
  if (home.bathrooms !== null) parts.push(`${home.bathrooms} bath`);
  if (home.squareFeet !== null) parts.push(`${home.squareFeet} sq ft`);
  return parts.length > 0 ? parts.join(', ') : null;
}

/** "Jo", "Jo and Maria", "Ana, Jo and Maria". */
function listed(names: readonly string[]): string {
  return names.length < 2
    ? (names[0] ?? '')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

const lowerFirst = (words: string): string => words.charAt(0).toLowerCase() + words.slice(1);

// The office's own page, so `me` is taken as every office screen takes it: whether Call through the
// business line places a call from this deployment (ops #84 phase 6) is read off it.
export function HomeDetail({ me }: { me: Me }) {
  const { id = '' } = useParams();
  const [record, setRecord] = useState<HomeRecord | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let live = true;
    setRecord(null);
    setError(null);
    getHome(id)
      .then((answer) => {
        if (live) setRecord(answer);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setError(
          thrown instanceof ApiError ? thrown : new ApiError(0, 'error', 'That home did not load.'),
        );
      });
    return () => {
      live = false;
    };
  }, [id, attempt]);

  if (error) {
    return (
      <div>
        <BackLink to="/clients" name="clients" />
        <ErrorNotice
          title="That home did not load"
          message={error.message}
          onRetry={() => setAttempt((n) => n + 1)}
        />
      </div>
    );
  }

  if (!record) {
    return (
      <div>
        <BackLink to="/clients" name="clients" />
        <Busy what="this home" />
      </div>
    );
  }

  const { client, home } = record;
  const returnPath = `/homes/${home.id}`;

  return (
    <div>
      <BackLink to={`/clients/${client.id}`} name={client.displayName} />

      <ScreenTitle title={home.label ?? home.addressLine1} />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Pill>{humanise(home.county)} County</Pill>
        {client.status === 'ended' ? (
          <Pill tone="gold">
            {client.endedOn
              ? `The household ended ${mediumDay(client.endedOn)}`
              : 'The household ended'}
          </Pill>
        ) : null}
      </div>

      <div className="xl:grid xl:grid-cols-[minmax(0,1fr)_400px] xl:items-start xl:gap-5">
        <div className="@container flex min-w-0 flex-col gap-4">
          <Section title="The home">
            <dl className="grid gap-4 @md:grid-cols-2 @2xl:grid-cols-3">
              <Detail label="Address" value={addressOf(home)} />
              <Detail label="Label" value={home.label} />
              <Detail label="Size" value={sizeOf(home)} />
              <Detail
                label="Last professional clean"
                value={
                  home.lastProfessionalCleanOn
                    ? `${mediumDay(home.lastProfessionalCleanOn)}${
                        home.lastProfessionalCleanBasis
                          ? `, ${humanise(home.lastProfessionalCleanBasis).toLowerCase()}`
                          : ''
                      }`
                    : null
                }
              />
              <Detail label="Pets" value={home.pets} />
              <Detail label="Getting to the door" value={home.entryNotes} />
              <Detail label="Gated community" value={home.communityName} />
              <Detail
                label="Vendor registration"
                value={
                  home.communityName || home.vendorRegistrationState !== 'not_required'
                    ? [
                        humanise(home.vendorRegistrationState),
                        home.vendorRegistrationExpiresOn
                          ? `until ${mediumDay(home.vendorRegistrationExpiresOn)}`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(', ')
                    : null
                }
              />
              <Detail label="Notes" value={home.notes} />
            </dl>
            <DoNotPanel text={home.doNotList} />
          </Section>

          <ChecksFirstSection
            siteId={home.id}
            checksFirst={home.checksFirst ?? null}
            onSaved={(checksFirst) =>
              setRecord((now) => (now ? { ...now, home: { ...now.home, checksFirst } } : now))
            }
          />

          <HomePlanSection key={home.id} siteId={home.id} />

          <div className="grid items-start gap-4 @xl:grid-cols-2">
            <Section
              title="Agreements"
              note="Each opens its own page, where a cadence changes, a pause is set or it ends."
            >
              <Agreements
                agreements={record.agreements}
                clientId={client.id}
                today={record.today}
              />
            </Section>

            <Section title="Codes" note="Each showing asks for a fresh sign-in.">
              <HomeCodes siteId={home.id} returnPath={returnPath} />
            </Section>
          </div>

          <Section
            title="Keys"
            note="Where each key the household gave is now, and how it got there."
          >
            <HomeKeys key={home.id} siteId={home.id} people={record.people} />
          </Section>

          <Section title="Next cleans">
            <NextCleans visits={record.visits} />
          </Section>

          <Section title="People">
            {record.people.length === 0 ? (
              <p className="text-sm text-coral">
                Nobody is on file for this household. There is nobody to tell when a visit moves.
              </p>
            ) : (
              <ul className="grid gap-4 @xl:grid-cols-2">
                {record.people.map((person) => (
                  <li key={person.id} className="flex flex-col gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-serif text-base leading-tight">{person.displayName}</h3>
                      <Pill>{humanise(person.role)}</Pill>
                    </div>
                    <div>
                      <CallLink
                        phoneE164={person.phoneE164}
                        marked={person.doNotContact.call ?? null}
                      />
                    </div>
                    {person.phoneE164 && !person.doNotContact.call ? (
                      <CallThrough
                        clientId={client.id}
                        contactId={person.id}
                        name={person.displayName}
                        on={me.placeCalls === true}
                      />
                    ) : null}
                    <MailLink email={person.email} />
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>

        {/* Keyed by the home, so walking from one home to the next never shows the history of the
            one before it while the next one loads. */}
        <div className="min-w-0">
          <TimelinePanel key={home.id} clientId={client.id} homeId={home.id} />
        </div>
      </div>
    </div>
  );
}

function Agreements({
  agreements,
  clientId,
  today,
}: {
  agreements: readonly AgreementLine[];
  clientId: string;
  today: string;
}) {
  if (agreements.length === 0) {
    return (
      <p className="text-sm text-navy/70">
        Nothing agreed for this home yet. The first one is set up on{' '}
        <Link to={`/clients/${clientId}`} className="font-medium underline">
          the household&apos;s page
        </Link>
        , priced off the published card.
      </p>
    );
  }
  return (
    <ul className="flex flex-col gap-3">
      {agreements.map((agreement) => (
        <li key={agreement.id}>
          <Link to={`/agreements/${agreement.id}`} className="flex flex-col gap-1">
            <span className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium underline">
                {SERVICE_LINE_LABELS[agreement.serviceLine]},{' '}
                {lowerFirst(CADENCE_LABELS[agreement.cadence])}, {money(agreement.priceCents)}
              </span>
              <Pill tone={agreementStateTone(agreement.state)}>
                {AGREEMENT_STATE_LABELS[agreement.state]}
              </Pill>
            </span>
            <span className="text-xs leading-4 text-navy/65">
              {PRICE_SOURCE_LABELS[agreement.priceSource]}
              {agreement.cardRevision === null ? '' : `, revision ${agreement.cardRevision}`}.{' '}
              {agreement.endedOn
                ? `From ${mediumDay(agreement.startedOn)} to ${mediumDay(agreement.endedOn)}.`
                : `${AGREEMENT_STATE_LABELS[agreement.state]} since ${mediumDay(agreement.startedOn)}.`}
              {pauseWords(agreement.pausedFrom, agreement.pausedUntil, today) === null
                ? ''
                : ` ${pauseWords(agreement.pausedFrom, agreement.pausedUntil, today)}`}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function NextCleans({ visits }: { visits: readonly OpenClean[] }) {
  if (visits.length === 0) {
    return <p className="text-sm text-navy/70">No clean of this home is booked ahead.</p>;
  }
  return (
    <ul className="flex flex-col">
      {visits.map((visit) => (
        <li key={visit.id} className="border-b border-navy/8 last:border-b-0">
          <Link
            to={`/visits/${visit.id}`}
            className="flex min-h-11 desk:min-h-9 items-center gap-3 py-1.5"
          >
            <span className="min-w-0 grow text-sm">
              {[
                shortDay(visit.scheduledDate),
                formatWindow(visit.windowStart, visit.windowEnd) ?? 'no window set',
                visit.cleaners.length > 0 ? `with ${listed(visit.cleaners)}` : 'nobody on it yet',
              ].join(', ')}
              {visit.reclean ? '. A re-clean' : ''}
            </span>
            <Pill tone={visit.status === 'in_progress' ? 'gold' : 'quiet'}>
              {VISIT_STATUS_LABELS[visit.status]}
            </Pill>
          </Link>
        </li>
      ))}
    </ul>
  );
}

/** One section of the page, as the client page draws one: the heading, a small word, the body. */
/**
 * WHAT THEY CHECK FIRST (ops #86): the three things the household said they look at first, as the home
 * keeps them and the next sheet will freeze them, and the office's edit of them on the household's
 * word. The server holds the rule; a refused claim is said under the boxes.
 */
function ChecksFirstSection({
  siteId,
  checksFirst,
  onSaved,
}: {
  siteId: string;
  checksFirst: string | null;
  onSaved: (checksFirst: string | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [lines, setLines] = useState<string[]>(() => checksFirstLines(checksFirst));
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<ApiError | null>(null);
  const [saved, setSaved] = useState(false);
  const boxes = useRefusedBoxes();
  const kept = checksFirst === null ? [] : checksFirst.split('\n');

  const save = async () => {
    setSaving(true);
    setFailure(null);
    setSaved(false);
    try {
      const answer = await setChecksFirst(siteId, joinChecksFirst(lines));
      onSaved(answer.checksFirst);
      setEditing(false);
      setSaved(true);
    } catch (thrown) {
      if (thrown instanceof ApiError) boxes.setRefused(refusedFieldsOf(thrown.body));
      setFailure(
        thrown instanceof ApiError ? thrown : new ApiError(0, 'error', 'That was not saved.'),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section title="They check first" note="Frozen onto each sheet when the cleaner opens it.">
      {editing ? (
        <div>
          <ChecksFirstBoxes
            idPrefix="home-checks-first"
            lines={lines}
            onChange={(next) => {
              setLines(next);
              boxes.clear('checksFirst');
            }}
            refused={boxes.refused.checksFirst}
          />
          {failure ? <ErrorNotice title="Not saved" message={failure.message} /> : null}
          <div className="mt-2 flex flex-wrap gap-2">
            <Button onClick={() => void save()} disabled={saving}>
              {saving ? 'Saving' : 'Save'}
            </Button>
            <Button
              tone="quiet"
              onClick={() => {
                setEditing(false);
                setFailure(null);
                setLines(checksFirstLines(checksFirst));
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {kept.length === 0 ? (
            <p className="text-sm text-navy/70">Nobody has asked them yet.</p>
          ) : (
            <ol className="list-decimal pl-5 text-sm">
              {kept.map((line, index) => (
                <li key={index}>{line}</li>
              ))}
            </ol>
          )}
          {saved ? <SavedNotice>Saved. The next sheet opened carries them.</SavedNotice> : null}
          <div>
            <Button
              tone="quiet"
              onClick={() => {
                setLines(checksFirstLines(checksFirst));
                setSaved(false);
                setEditing(true);
              }}
            >
              {kept.length === 0 ? 'Add them' : 'Change them'}
            </Button>
          </div>
        </div>
      )}
    </Section>
  );
}

/** "Version 2, approved by Victoria on Sep 8, 2026. Taken down on a call." */
function versionLine(version: HomePlanVersion): string {
  const source = HOME_PLAN_SOURCE_LABELS[version.source];
  if (version.approvedAt === null) {
    return `Draft, version ${version.version}, on no sheet until it is approved. ${source}.`;
  }
  const by = version.approvedByName ? ` by ${version.approvedByName}` : '';
  const on = version.effectiveFrom ? ` on ${mediumDay(version.effectiveFrom)}` : '';
  return `Version ${version.version}, approved${by}${on}. ${source}.`;
}

/**
 * THE HOME IN THE HOUSEHOLD'S OWN WORDS (ops #74 item 6): the version approved, frozen onto each sheet
 * at its first open, and the draft beside it, which no sheet shows. The office types a new version
 * from a call or a walkthrough, in place of a draft that stands, and approves the draft it read, by its
 * id; the server refuses a code in it and holds every rule. Words as the household gave them: drawn as
 * typed, never tidied.
 */
function HomePlanSection({ siteId }: { siteId: string }) {
  const [plan, setPlan] = useState<HomePlan | null>(null);
  const [failed, setFailed] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState('');
  const [source, setSource] = useState<OfficeHomePlanSource | ''>('');
  const [busy, setBusy] = useState(false);
  const [refused, setRefused] = useState<ApiError | null>(null);
  const [said, setSaid] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setPlan(null);
    setFailed(null);
    getHomePlan(siteId)
      .then((answer) => {
        if (live) setPlan(answer);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setFailed(
          thrown instanceof ApiError
            ? thrown
            : new ApiError(0, 'error', 'The home plan did not load.'),
        );
      });
    return () => {
      live = false;
    };
  }, [siteId, attempt]);

  const perform = async (work: () => Promise<HomePlan>, done: string) => {
    setBusy(true);
    setRefused(null);
    setSaid(null);
    try {
      setPlan(await work());
      setEditing(false);
      setSaid(done);
    } catch (thrown) {
      setRefused(
        thrown instanceof ApiError ? thrown : new ApiError(0, 'error', 'That was not saved.'),
      );
    } finally {
      setBusy(false);
    }
  };

  const note =
    'In their own words. The approved version is frozen onto each sheet when it is opened.';

  if (failed) {
    return (
      <Section title="Home plan" note={note}>
        <ErrorNotice
          title="The home plan did not load"
          message={failed.message}
          onRetry={() => setAttempt((n) => n + 1)}
        />
      </Section>
    );
  }
  if (!plan) {
    return (
      <Section title="Home plan" note={note}>
        <Busy what="the home plan" />
      </Section>
    );
  }

  const { live, draft } = plan;
  const start = () => {
    const from = draft ?? live;
    setBody(from?.body ?? '');
    setSource(
      draft && (OFFICE_HOME_PLAN_SOURCES as readonly string[]).includes(draft.source)
        ? (draft.source as OfficeHomePlanSource)
        : '',
    );
    setRefused(null);
    setSaid(null);
    setEditing(true);
  };

  return (
    <Section title="Home plan" note={note}>
      {live ? (
        <div data-home-plan="live" className="flex flex-col gap-1">
          <p className="text-xs leading-4 text-navy/65">{versionLine(live)}</p>
          <p className="whitespace-pre-wrap text-sm">{live.body}</p>
        </div>
      ) : (
        <p className="text-sm text-navy/70">
          No version is approved yet, so a sheet opened now carries none.
        </p>
      )}
      {draft && !editing ? (
        <div data-home-plan="draft" className="flex flex-col gap-2 border-l-4 border-l-gold pl-3">
          <p className="text-xs leading-4 text-navy/65">{versionLine(draft)}</p>
          <p className="whitespace-pre-wrap text-sm">{draft.body}</p>
          <div>
            <Button
              onClick={() =>
                void perform(
                  () => approveHomePlan(siteId, draft.id),
                  'Approved. The next sheet opened carries it.',
                )
              }
              disabled={busy}
            >
              {busy ? 'Approving' : `Approve version ${draft.version}`}
            </Button>
          </div>
        </div>
      ) : null}
      {editing ? (
        <div className="flex flex-col gap-3">
          <TextAreaField
            id="home-plan-body"
            label="The plan, in their words"
            value={body}
            onChange={setBody}
            rows={6}
            hint="Their rooms, what matters to them and what they wish for, as they said it. At most 4000 characters, and never a code: codes are kept with the home's codes."
          />
          <SelectField
            id="home-plan-source"
            label="Where the words came from"
            value={source}
            onChange={setSource}
            options={OFFICE_HOME_PLAN_SOURCES}
            toLabel={(value) => HOME_PLAN_SOURCE_LABELS[value]}
            placeholder="Choose one"
            required
          />
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => {
                if (source === '') return;
                void perform(
                  () => writeHomePlan(siteId, { body, source }),
                  'Saved as a draft. No sheet shows it until it is approved.',
                );
              }}
              disabled={busy || body.trim() === '' || source === ''}
            >
              {busy ? 'Saving' : 'Save as a draft'}
            </Button>
            <Button
              tone="quiet"
              onClick={() => {
                setEditing(false);
                setRefused(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <Button tone="quiet" onClick={start}>
            {live || draft ? 'Write a new version' : 'Type the plan'}
          </Button>
        </div>
      )}
      {refused ? <ErrorNotice title="Not saved" message={refused.message} /> : null}
      {said ? <SavedNotice>{said}</SavedNotice> : null}
    </Section>
  );
}

function Section({ title, note, children }: { title: string; note?: string; children: ReactNode }) {
  return (
    <Card className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline gap-2">
        <h2 className="font-serif text-lg leading-7">{title}</h2>
        {note ? <span className="text-xs leading-4 text-navy/65">{note}</span> : null}
      </div>
      {children}
    </Card>
  );
}

function BackLink({ to, name }: { to: string; name: string }) {
  return (
    <Link
      to={to}
      className="mb-2 -ml-2 inline-flex min-h-11 desk:min-h-9 items-center gap-1 px-2 text-sm font-medium text-navy/70"
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 20 20"
        className="h-4 w-4 shrink-0"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 5l-5 5 5 5" />
      </svg>
      Back to {name}
    </Link>
  );
}
